/**
 * Voice AI — Authentication Service
 * Manages Bearer tokens, user state, and API auth calls
 * Storage: sessionStorage (cleared on tab close)
 */

const TOKEN_KEY = 'voice_token';
const USER_KEY = 'voice_user';
const EXPIRES_KEY = 'voice_token_expires';
const API_BASE = '/voice/php/api';

class AuthService {
  constructor() {
    this._listeners = new Set();
  }

  /**
   * Login with username/email + password
   * @returns {Promise<{success: boolean, user?: object, error?: string, errorCode?: string}>}
   */
  async login(username, password, rememberMe = false) {
    try {
      const res = await fetch(`${API_BASE}/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, remember_me: rememberMe })
      });

      const data = await res.json();

      if (!data.success) {
        return { success: false, error: data.error || 'Login failed', errorCode: data.error_code || '' };
      }

      // Store token and user
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem(TOKEN_KEY, data.token);
      storage.setItem(USER_KEY, JSON.stringify(data.user));
      storage.setItem(EXPIRES_KEY, data.expires_at);

      this._notify();
      return { success: true, user: data.user };
    } catch (err) {
      console.error('[Auth] Login error:', err);
      return { success: false, error: 'Network error — please try again' };
    }
  }

  /**
   * Logout — invalidate token on server, clear local storage
   */
  async logout() {
    const token = this.getToken();
    if (token) {
      try {
        await fetch(`${API_BASE}/logout.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({})  // ModSecurity requires non-empty body
        });
      } catch (err) {
        console.error('[Auth] Logout API error:', err);
      }
    }

    // Clear both storages
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(EXPIRES_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(EXPIRES_KEY);

    this._notify();
  }

  /**
   * Get stored token (checks both storages)
   */
  getToken() {
    return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || null;
  }

  /**
   * Get stored user object
   */
  getUser() {
    const raw = sessionStorage.getItem(USER_KEY) || localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * Check if user is authenticated (token exists and not expired)
   */
  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;

    const expires = sessionStorage.getItem(EXPIRES_KEY) || localStorage.getItem(EXPIRES_KEY);
    if (expires && new Date(expires) < new Date()) {
      // Token expired — set reason BEFORE logout so login-page can read it
      this._sessionExpiredReason = 'Your session has expired. Please log in again.';
      this.logout();
      return false;
    }

    return true;
  }

  /**
   * Validate token against server (used on app init)
   * @returns {Promise<{valid: boolean, user?: object}>}
   */
  async validateToken() {
    const token = this.getToken();
    if (!token) return { valid: false };

    try {
      const res = await fetch(`${API_BASE}/user.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        this.logout();
        return { valid: false };
      }

      const data = await res.json();
      if (data.success && data.user) {
        // Refresh stored user data
        const storage = localStorage.getItem(TOKEN_KEY) ? localStorage : sessionStorage;
        storage.setItem(USER_KEY, JSON.stringify(data.user));
        return { valid: true, user: data.user };
      }

      this.logout();
      return { valid: false };
    } catch (err) {
      console.error('[Auth] Token validation error:', err);
      return { valid: false };
    }
  }

  /**
   * Centralized fetch wrapper — intercepts 401/403 and triggers logout
   * Use this instead of raw fetch() for all authenticated API calls
   * @param {string} url
   * @param {object} opts - standard fetch options
   * @returns {Promise<Response>}
   */
  async apiFetch(url, opts = {}) {
    if (!this.isAuthenticated()) {
      throw new Error('Session expired');
    }

    const token = this.getToken();
    opts.headers = { ...opts.headers };
    if (token) {
      opts.headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(url, opts);

    if (res.status === 401 || res.status === 403) {
      this._sessionExpiredReason = 'Your session has expired. Please log in again.';
      await this.logout();
      throw new Error('Session expired');
    }

    return res;
  }

  /**
   * Get and clear the session expired reason (for login page to display)
   */
  getSessionExpiredMessage() {
    const msg = this._sessionExpiredReason || null;
    this._sessionExpiredReason = null;
    return msg;
  }

  /**
   * Get user initials for avatar
   */
  getUserInitials() {
    const user = this.getUser();
    if (!user) return '?';
    const name = user.display_name || user.login || user.email || '';
    if (!name) return '?';
    const parts = name.split(/[\s.]+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  }

  /**
   * Get display name
   */
  getDisplayName() {
    const user = this.getUser();
    if (!user) return '';
    return user.display_name || user.login || user.email;
  }

  /**
   * Subscribe to auth state changes
   */
  onChange(callback) {
    this._listeners.add(callback);
    return () => this._listeners.delete(callback);
  }

  _notify() {
    const authenticated = this.isAuthenticated();
    const user = this.getUser();
    this._listeners.forEach(cb => {
      try { cb({ authenticated, user }); } catch (e) { console.error(e); }
    });
  }
}

// Singleton
export const authService = new AuthService();
